import { useState, useEffect } from 'react';

export const RuleEditor = ({ 
    ruleData, 
    broadcasterRules = [], 
    regulationTexts = [],
    onSave, 
    onCancel,
    onDelete,
    onStartPickParent,
    onStartPickStack,
    isPickingParent,
    isPickingStack
}) => {
    const [formData, setFormData] = useState(null);

    useEffect(() => {
        if (ruleData) {
            setFormData(prev => {
                // Check if we are still editing the same rule 
                // (This works even for new rules where id might be null or undefined)
                const isSameRule = prev && prev.id === ruleData.id;

                if (isSameRule) {
                    // If it's the same rule, ONLY update the parent or stack ID.
                    // This preserves all the text you've currently typed in the form!
                    return {
                        ...prev,
                        parent_id: ruleData.parent_id !== undefined ? ruleData.parent_id : prev.parent_id,
                        stack_id: ruleData.stack_id !== undefined ? ruleData.stack_id : prev.stack_id,
                    };
                }

                // If it's a completely newly selected rule from the chart, do the full initialization
                const initialPeriods = ruleData.application_periods?.length > 0 
                    ? ruleData.application_periods 
                    : [{ start_year: 2025, end_year: null, value: ruleData.value_yearly || '' }];

                return {
                    ...ruleData,
                    parent_id: ruleData.parent_id || '',
                    stack_id: ruleData.stack_id || '',
                    relevant_text: ruleData.relevant_text || '',
                    period: ruleData.period || null,
                    value_unit: ruleData.value_unit || '',
                    value_type: ruleData.value_type || '',
                    application_periods: initialPeriods
                };
            });
        } else {
            setFormData(null);
        }
    }, [ruleData]);

    const handlePeriodValueChange = (index, field, val) => {
        const updatedPeriods = [...formData.application_periods];
        // Transformation en nombre si applicable, sauf si end_year est vide
        let finalVal = val;
        if (field !== 'value' || formData.family !== "Obligations de diffusion") {
            if (val === '') finalVal = null;
            else if (!isNaN(val)) finalVal = parseFloat(val);
        }
        updatedPeriods[index] = { ...updatedPeriods[index], [field]: finalVal };
        setFormData({ ...formData, application_periods: updatedPeriods });
    };

    const addApplicationPeriod = () => {
        setFormData({ 
            ...formData, 
            application_periods: [...formData.application_periods, { start_year: 2024, end_year: null, value: 0 }] 
        });
    };

    const duplicatePeriod = (index) => {
        const periodToCopy = formData.application_periods[index];
        setFormData({
            ...formData,
            application_periods: [...formData.application_periods, { ...periodToCopy, start_year: (periodToCopy.end_year || periodToCopy.start_year) + 1 }]
        });
    };

    const removeApplicationPeriod = (index) => {
        const updatedPeriods = formData.application_periods.filter((_, i) => i !== index);
        setFormData({ ...formData, application_periods: updatedPeriods });
    };

    const getRuleName = (id) => {
        if (!id) return "";
        const found = broadcasterRules.find(r => r.id === id);
        return found ? (found.rule || found.rule_type) : `ID: ${id}`;
    };

    const getStackName = (stackId) => {
        if (!stackId) return "";
        const sibling = broadcasterRules.find(r => r.stack_id === stackId && r.id !== formData?.id);
        if (sibling) return `Avec: ${sibling.rule || sibling.rule_type}`;
        return "Nouvelle pile / Pile vide";
    };

    const handleChange = (e) => {
        if (!formData) return;
        const { name, value } = e.target;
        const finalValue = (name === 'relevant_text' && !isNaN(Number(value)) && value !== "") 
            ? Number(value) 
            : value;
        setFormData({ ...formData, [name]: finalValue });
    };

    const handlePeriodChange = (field, subField, value) => {
        if (!formData) return;
        const currentPeriod = formData.period || {};
        let updatedPeriod = { ...currentPeriod };

        if (subField) {
            const val = (subField === 'value_yearly' && value === '') ? null : (subField === 'value_yearly' ? parseFloat(value) : value);
            updatedPeriod.value_info = {
                ...updatedPeriod.value_info,
                [subField]: val
            };
        } else {
            updatedPeriod[field] = value;
        }
        setFormData({ ...formData, period: updatedPeriod });
    };

    // --- FIX: Clean payload (remove undefined) and submit ---
    const handleSubmit = (e) => {
        e.preventDefault();
        console.log("Form submitted", formData); // Debug log

        if (formData) {
            // 1. Create copy
            const payload = { ...formData };

            // 2. Sync value_yearly for broadcast
            if (formData.family === "Obligations de diffusion") {
                payload.value_yearly = formData.value_info?.value_yearly;
            }

            // 3. Sanitize: Firestore doesn't like 'undefined' in arrays
            const cleanPayload = JSON.parse(JSON.stringify(payload));

            onSave(cleanPayload);
        }
    };

    const handleDelete = () => {
        if (!formData || formData.id === null || formData.id === undefined) return;
        if (window.confirm("Êtes-vous sûr de vouloir supprimer cette règle ?")) {
            onDelete(formData.id);
        }
    };

    if (!formData) return <div className="editor-placeholder">Sélectionnez une règle sur la visualisation à gauche pour la modifier</div>;

    const hasId = formData.id !== null && formData.id !== undefined;

    return (
        <div className="rule-editor">
            {/* <h3>Éditer : {formData.rule || formData.rule_type}</h3> */}
            <form onSubmit={handleSubmit}>
                
                {formData.family !== "Obligations de diffusion" && (
                    <div className="form-group">
                        <label htmlFor="form-rule-name">Nom<br/><span className="sublabel">S'affiche sur la visualisation</span></label>
                        <input type="text" name="rule" id="form-rule-name" value={formData.rule || ''} onChange={handleChange} />
                    </div>
                )}

                <div className="form-flex-group">
                    <div className="form-group">
                        <label htmlFor="form-rule-type">Type d'obligation<br/><span className="sublabel">Classification interne, n'apparaît pas sur la visualisation</span></label>
                        <input type="text" name="rule_type" id="form-rule-type" value={formData.rule_type || ''} onChange={handleChange} />
                    </div>

                    <div className="form-group">
                        <label htmlFor="form-rule-object">Objet<br/><span className="sublabel">Catégorie d'œuvres ou d'investissement concernée par l'obligation</span></label>
                        <input type="text" name="object" id="form-rule-object" value={formData.object || ''} onChange={handleChange} />
                    </div>
                </div>

                {formData.family === "Obligations de financement" && (
                    <>
                        <div className="form-flex-group">
                            <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', gap: '5px' }}>
                                <div style={{ flex: 1 }}>
                                    <label htmlFor="form-shareof">Part de...</label>
                                    <input type="text" id="form-shareof" readOnly value={getRuleName(formData.parent_id)} placeholder="Aucun parent" className={isPickingParent ? "pulsating-border" : (formData.parent_id ? "dashed-border" : "")} />
                                </div>
                                <button type="button" className={isPickingParent ? "active-pick-btn" : "pick-btn"} onClick={onStartPickParent} style={{ backgroundColor: isPickingParent ? '#fde68a' : '#979797' }}>
                                    {isPickingParent ? 'Annuler' : 'Cibler'}
                                </button>
                            </div>
                            <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', gap: '5px' }}>
                                <div style={{ flex: 1 }}>
                                    <label htmlFor="form-stack">Se cumule avec...</label>
                                    <input type="text" id="form-stack" readOnly value={getStackName(formData.stack_id)} placeholder="Aucun cumul" className={isPickingStack ? "pulsating-border" : ""} />
                                </div>
                                <button type="button" className={isPickingStack ? "active-pick-btn" : "pick-btn"} onClick={onStartPickStack} style={{ backgroundColor: isPickingStack ? '#fde68a' : '#979797' }}>
                                    {isPickingStack ? 'Annuler' : 'Cibler'}
                                </button>
                            </div>
                        </div>
                        <div className="form-group">
                            <label htmlFor="form-description">Description<br/><span className="sublabel">S'affiche en-dessous du nom lorsque l'utilisateur passe la souris dessus</span></label>
                            <input type="text" id="form-description" name="description" value={formData.description || ''} onChange={handleChange} />
                        </div>
                    </>
                )}


                <div className="form-group">
                    <label htmlFor="form-relevant-text">Texte de référence</label>
                    <select name="relevant_text" id="form-relevant-text" value={formData.relevant_text || ''} onChange={handleChange} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}>
                        <option value="">Aucun / Non défini</option>
                        {regulationTexts.map((text, index) => (
                            <option key={text.text_id || index} value={text.text_id}>{text.text_name}</option>
                        ))}
                    </select>
                </div>

                <fieldset className="rule-values-section">
                    <legend>Configuration de la règle</legend>

                    {/* Métadonnées de la règle (Unité et Type) */}
                    <div className="form-flex-group" style={{ marginBottom: '20px', borderBottom: '1px solid #eee', paddingBottom: '15px' }}>
                        <div className="form-group">
                            <label htmlFor="form-value-unit">Unité</label>
                            <select name="value_unit" id="form-value-unit" value={formData.value_unit} onChange={handleChange}>
                                <option value="">Aucune</option>
                                <option value="share">Part (%)</option>
                                <option value="M€">M€</option>
                                <option value="films">Films</option>
                                <option value="series">Séries</option>
                                <option value="programs">Programmes</option>
                                <option value="jours">Jours</option>
                                <option value="mois">Mois</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label htmlFor="form-value-type">Type de valeur</label>
                            <select name="value_type" id="form-value-type" value={formData.value_type} onChange={handleChange}>
                                <option value="exact">Valeur exacte</option>
                                <option value="min">Minimum</option>
                                <option value="max">Maximum</option>
                            </select>
                        </div>
                    </div>

                    {/* Liste des périodes d'application */}
                    <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '10px' }}>Périodes et valeurs</label>
                    {formData.application_periods.map((period, index) => (
                        <div key={index} className="period-card" style={{ 
                            background: '#f9f9f9', 
                            padding: '12px', 
                            marginBottom: '10px', 
                            borderRadius: '6px',
                            border: '1px solid #ddd'
                        }}>
                            <div className="form-flex-group" style={{ alignItems: 'flex-start' }}>
                                <div className="form-group">
                                    <label className="sublabel">Début</label>
                                    <input type="number" value={period.start_year || ''} onChange={(e) => handlePeriodValueChange(index, 'start_year', e.target.value)} />
                                </div>
                                <div className="form-group">
                                    <label className="sublabel">Fin (optionnel)</label>
                                    <input type="number" placeholder="En cours" value={period.end_year || ''} onChange={(e) => handlePeriodValueChange(index, 'end_year', e.target.value)} />
                                </div>
                                <div className="form-group" style={{ flex: 2 }}>
                                    <label className="sublabel">Valeur ({formData.value_unit === 'share' ? '%' : formData.value_unit})</label>
                                    {formData.family === "Obligations de diffusion" ? (
                                        <textarea value={period.value || ''} onChange={(e) => handlePeriodValueChange(index, 'value', e.target.value)} />
                                    ) : (
                                        <input type="number" step="any" value={period.value ?? ''} onChange={(e) => handlePeriodValueChange(index, 'value', e.target.value)} />
                                    )}
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                                <button type="button" onClick={() => duplicatePeriod(index)} className="secondary-btn" style={{ fontSize: '0.8em' }}>Dupliquer</button>
                                <button type="button" onClick={() => removeApplicationPeriod(index)} style={{ color: '#dc3545', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8em' }}>Supprimer</button>
                            </div>
                        </div>
                    ))}
                    <button type="button" onClick={addApplicationPeriod} className="add-btn" style={{ width: '100%', marginTop: '5px' }}>+ Ajouter une période</button>
                </fieldset>

                {formData.family === "Chronologie des médias" && (
                    <fieldset className="period-fieldset" style={{marginTop: '20px', border: '1px solid #e6c200'}}>
                        <legend style={{color: '#bfa200', fontWeight: 'bold'}}>Période associée</legend>
                        <div className="form-group">
                            <label htmlFor="form-period-type">Type de période</label>
                            <input type="text" id="form-period-type" placeholder="ex: Replay" value={formData.period?.rule_type || ''} onChange={(e) => handlePeriodChange('rule_type', null, e.target.value)} />
                        </div>
                        <div className="form-group">
                            <label htmlFor="form-period-duration">Durée</label>
                            <input type="number" id="form-period-duration" placeholder="ex: 30" value={formData.period?.value_info?.value_yearly === null ? '' : formData.period?.value_info?.value_yearly} onChange={(e) => handlePeriodChange('value_info', 'value_yearly', e.target.value)} />
                        </div>
                        <div className="form-group">
                            <label htmlFor="form-period-unit">Unité</label>
                            <input type="text" id="form-period-unit" placeholder="ex: jours" value={formData.period?.value_info?.value_unit || ''} onChange={(e) => handlePeriodChange('value_info', 'value_unit', e.target.value)} />
                        </div>
                    </fieldset>
                )}

                <div className="form-actions" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <button type="submit" className="save-btn">Enregistrer</button>
                    <button type="button" onClick={onCancel} className="cancel-btn">Annuler</button>
                    <button type="button" onClick={handleDelete} className="delete-btn" disabled={!hasId} style={{ marginLeft: 'auto', backgroundColor: hasId ? '#dc3545' : '#e2e6ea', color: hasId ? 'white' : '#aaa', cursor: hasId ? 'pointer' : 'not-allowed' }}>Supprimer</button>
                </div>
            </form>
        </div>
    );
};